// The website chat, browser side. Talks to the web-chat edge function and
// decides which devices get the on-screen chat: a phone or tablet keeps
// opening the visitor's own WhatsApp (owner rule, 6 Oct 2026).
import { supabase } from '@/lib/supabaseClient';

/** Phones and tablets keep WhatsApp itself; everything else gets the on-screen chat. */
export function isMobileDevice(
    nav = typeof navigator !== 'undefined' ? navigator : null,
    mm = typeof window !== 'undefined' && window.matchMedia ? (q) => window.matchMedia(q) : null,
) {
    const ua = String(nav?.userAgent || '');
    if (/Android|iPhone|iPad|iPod|Mobile|Silk|Kindle|BlackBerry|Opera Mini|IEMobile/i.test(ua)) return true;
    // iPadOS presents itself as a Mac; its touch screen gives it away.
    if (/Macintosh/.test(ua) && Number(nav?.maxTouchPoints) > 1) return true;
    try {
        if (mm && mm('(pointer: coarse)').matches && !mm('(pointer: fine)').matches) return true;
    } catch { /* no media queries: treat as a computer */ }
    return false;
}

/** A UK mobile in any common shape → '+447…', or ''. Mirrors ukMobile in supabase/functions/_shared/webChat.ts. */
export function ukMobile(raw) {
    let d = String(raw ?? '').replace(/\D/g, '');
    if (d.startsWith('00')) d = d.slice(2);
    if (d.startsWith('44')) d = d.slice(2);
    if (d.startsWith('0')) d = d.slice(1);
    return /^7\d{9}$/.test(d) ? `+44${d}` : '';
}

/** +447700900123 → 07700 900123. */
export function displayMobile(e164) {
    const m = /^\+44(7\d{3})(\d{6})$/.exec(String(e164 || ''));
    return m ? `0${m[1]} ${m[2]}` : String(e164 || '');
}

// The opening pills: pick what you want to know, then give name and mobile,
// and the question goes to the FAQ assistant the moment the chat opens.
// recruitment_cms.whatsapp_chat.topics ([{label, text}]) replaces these.
export const DEFAULT_TOPICS = [
    { label: 'Pay & hours', text: 'How much is the pay, and what are the hours?' },
    { label: 'Nearest outbase', text: 'Which outbase is nearest to me?' },
    { label: 'Van & equipment', text: 'Do I need my own van?' },
    { label: 'Training', text: 'How does the training work?' },
    { label: 'My application', text: 'Where am I with my application?' },
    { label: 'Book a call', text: "I'd like to book a call with the team.", button: 'call' },
];

export const MAX_TOPICS = 8;

/** The topics as saved: a label and a question each, at most 8; "books a call" kept as the `call` button. */
export function cleanTopics(list) {
    return (Array.isArray(list) ? list : [])
        .filter((t) => t && typeof t.label === 'string' && typeof t.text === 'string' && t.label.trim() && t.text.trim())
        .map((t) => ({ label: t.label.trim().slice(0, 40), text: t.text.trim().slice(0, 300), ...(t.button === 'call' ? { button: 'call' } : {}) }))
        .slice(0, MAX_TOPICS);
}

/** What the chat shows: the saved topics, or the defaults when none are saved. */
export function topicsFrom(cfgTopics) {
    const list = cleanTopics(cfgTopics);
    return list.length ? list : DEFAULT_TOPICS;
}

// Own names, so every browser writes "Tue 13 Oct" the way the assistant does.
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const UK = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short', hour: 'numeric', minute: '2-digit', hour12: false,
});

/** A moment in UK time, as the assistant writes it: day "Tue 13 Oct", time "11:20am". */
function ukParts(iso) {
    const parts = UK.formatToParts(new Date(iso));
    const get = (t) => parts.find((p) => p.type === t)?.value ?? '';
    const wd = WD.find((w) => get('weekday').startsWith(w)) ?? get('weekday');
    const h = Number(get('hour')) % 24;
    const m = get('minute');
    return {
        key: `${get('year')}-${get('month').padStart(2, '0')}-${get('day').padStart(2, '0')}`,
        day: `${wd} ${Number(get('day'))} ${MON[Number(get('month')) - 1] ?? get('month')}`,
        time: `${h % 12 === 0 ? 12 : h % 12}${m === '00' ? '' : `:${m}`}${h >= 12 ? 'pm' : 'am'}`,
    };
}

/**
 * "More times": the free call slots (owner, 9 Oct 2026: the call times are the
 * team's published slots, at any hour) grouped by UK day, in time order.
 */
export function slotDays(slots) {
    const days = new Map();
    for (const s of (Array.isArray(slots) ? slots : [])) {
        if (!s?.id || !Number.isFinite(Date.parse(s.starts_at))) continue;
        const p = ukParts(s.starts_at);
        if (!days.has(p.key)) days.set(p.key, { value: p.key, label: p.day, slots: [] });
        days.get(p.key).slots.push({ id: s.id, label: p.time, starts_at: s.starts_at });
    }
    return [...days.values()]
        .sort((a, b) => a.value.localeCompare(b.value))
        .map((d) => ({ ...d, slots: d.slots.sort((a, b) => String(a.starts_at).localeCompare(String(b.starts_at))) }));
}

/** The message and button payload for a slot picked in "More times". */
export function slotMessage(day, slot) {
    return { text: `Please call me on ${day.label} at ${slot.label}.`, button: `slot:${slot.id}` };
}

/** New messages folded into the list: by id, in time order. */
export function mergeMessages(list, incoming) {
    const byId = new Map((list || []).map((m) => [m.id, m]));
    for (const m of incoming || []) if (m?.id) byId.set(m.id, m);
    return [...byId.values()].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
}

// Remembered so a reload, or coming back later, reopens the same chat.
// Browser storage can be missing or blocked; the chat then simply starts over.
const STORE = 'parcelink.webchat.v1';
export function loadChat() {
    try {
        const v = JSON.parse(window.localStorage.getItem(STORE) || 'null');
        return v && typeof v.token === 'string' ? v : null;
    } catch {
        return null;
    }
}
export function saveChat(v) {
    try {
        if (v) window.localStorage.setItem(STORE, JSON.stringify(v));
        else window.localStorage.removeItem(STORE);
    } catch { /* storage blocked */ }
}

/** One call to web-chat. Always resolves to { ok, ... } with a readable error. */
export async function webChat(action, payload = {}) {
    const { data, error } = await supabase.functions.invoke('web-chat', { body: { action, ...payload } });
    if (!error) return data || { ok: false, error: 'No reply from the chat.' };
    try {
        const body = await error.context?.json?.();
        if (body && typeof body === 'object') return body;
    } catch { /* not JSON */ }
    return { ok: false, code: 'network', error: 'Could not reach the chat. Please try again.' };
}
